/**
 * RuteoPruebas.jsx
 * 
 * Página principal de pruebas de ruteo y simulación
 * 
 * Qué hace:
 * - Permite ingresar dirección inicial y múltiples direcciones de entrega
 * - Geocodifica todas las direcciones
 * - Calcula la ruta optimizada usando OSRM
 * - Muestra la ruta en un mapa interactivo
 * - Simula el movimiento del repartidor (automático o manual)
 * - Detecta cuando el repartidor está "en camino" o "cerca del destino"
 * - Muestra notificaciones simuladas de WhatsApp
 * 
 * Qué recibe:
 * - No recibe props (es una página)
 * 
 * Qué devuelve:
 * - JSX con la interfaz completa de pruebas
 * 
 * Dónde modificarlo:
 * - Para agregar más campos de entrada
 * - Para cambiar la lógica de simulación
 * - Para modificar el diseño de la interfaz
 */

import React, { useState, useEffect, useRef } from 'react'
import { useSimulacion } from '../context/SimulacionContext'
import MapaRutas from '../components/MapaRutas'
import { geocodificar, geocodificarMultiples } from '../services/geocodificacion'
import { calcularRutaOptimizada } from '../services/ruteoOSRM'
import './RuteoPruebas.css'

function RuteoPruebas() {
  // Usar el contexto de simulación para compartir estado
  const {
    posicionRepartidor,
    setPosicionRepartidor,
    ruta,
    setRuta,
    puntosEntrega,
    setPuntosEntrega,
    puntoInicial,
    setPuntoInicial,
    simulando,
    setSimulando,
    velocidad,
    setVelocidad,
    obtenerIdEntrega,
    limpiarIds
  } = useSimulacion()

  // Estado para la dirección inicial
  const [direccionInicial, setDireccionInicial] = useState('')

  // Estado para las direcciones de entrega (lista editable)
  const [direccionesEntrega, setDireccionesEntrega] = useState([''])

  // Estado para ajuste manual de puntos
  const [puntoAjustando, setPuntoAjustando] = useState(null) // {tipo: 'inicial'|'entrega', index: number}

  // Estado local complementario (no compartido con seguimiento)
  const [ordenOptimizado, setOrdenOptimizado] = useState([])
  const [distanciaTotal, setDistanciaTotal] = useState(0)
  const [duracionTotal, setDuracionTotal] = useState(0)
  const [modoSimulacion, setModoSimulacion] = useState('automatico') // 'automatico' o 'manual'
  const [indiceRutaActual, setIndiceRutaActual] = useState(0)
  const [puntoActualEntrega, setPuntoActualEntrega] = useState(-1)

  // Estado para las notificaciones
  const [notificaciones, setNotificaciones] = useState([])
  
  // Estado para rastrear qué destinos ya recibieron notificación de "cerca"
  // Usamos un Set para evitar duplicados
  const destinosNotificadosRef = useRef(new Set())

  // Referencias para controlar la simulación
  const intervaloSimulacionRef = useRef(null)
  const animacionRef = useRef(null)

  // Estado de carga
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState(null)

  // Estado para seleccionar el proveedor de geocodificación
  const [proveedorGeocodificacion, setProveedorGeocodificacion] = useState('auto')

  /**
   * Agrega un nuevo campo de dirección de entrega
   */
  const agregarDireccion = () => {
    setDireccionesEntrega([...direccionesEntrega, ''])
  }

  /**
   * Elimina un campo de dirección de entrega
   */
  const eliminarDireccion = (index) => {
    const nuevas = direccionesEntrega.filter((_, i) => i !== index)
    setDireccionesEntrega(nuevas)
  }

  /**
   * Actualiza una dirección de entrega en la lista
   */
  const actualizarDireccion = (index, valor) => {
    const nuevas = [...direccionesEntrega]
    nuevas[index] = valor
    setDireccionesEntrega(nuevas)
  }

  /**
   * Geocodifica todas las direcciones y calcula la ruta optimizada
   */
  const calcularRuta = async () => {
    setCargando(true)
    setError(null)
    setRuta([])
    setPuntosEntrega([])
    setPosicionRepartidor(null)
    setIndiceRutaActual(0)
    setPuntoActualEntrega(-1)
    setNotificaciones([])
    // Limpiar el registro de destinos notificados
    destinosNotificadosRef.current.clear()

    try {
      // Validar dirección inicial
      if (!direccionInicial.trim()) {
        throw new Error('Debes ingresar una dirección inicial')
      }

      // Filtrar direcciones vacías
      const direccionesValidas = direccionesEntrega.filter(dir => dir.trim() !== '')
      if (direccionesValidas.length === 0) {
        throw new Error('Debes ingresar al menos una dirección de entrega')
      }

      // Configurar opciones de geocodificación
      // Viewbox aproximado para San Miguel de Tucumán (solo para Nominatim)
      const viewboxTucuman = [-65.25, -26.85, -65.15, -26.75] // [minLng, minLat, maxLng, maxLat]
      
      const opcionesGeocodificacion = {
        proveedor: proveedorGeocodificacion, // Usar el proveedor seleccionado
        viewbox: viewboxTucuman, // Limita la búsqueda a esta área (solo Nominatim)
        pais: 'ar' // Código de país ISO (Argentina)
      }

      // Geocodificar dirección inicial (sin notificaciones durante simulación)
      const puntoIni = await geocodificar(direccionInicial, opcionesGeocodificacion)
      setPuntoInicial(puntoIni)

      // Geocodificar todas las direcciones de entrega con delays
      const puntos = await geocodificarMultiples(direccionesValidas, opcionesGeocodificacion)
      
      // Agregar número de orden y generar ID único para cada punto
      const puntosConNumero = puntos.map((punto, index) => {
        const idEntrega = obtenerIdEntrega(index)
        return {
          ...punto,
          numero: index + 1,
          id: idEntrega // ID único para el enlace de seguimiento
        }
      })
      
      // Debug: verificar los puntos antes de guardarlos
      console.log('Puntos con IDs generados:', puntosConNumero.map(p => ({ id: p.id, direccion: p.direccion })))
      
      // Obtener IDs del contexto actual ANTES de setPuntosEntrega
      const idsMap = new Map()
      puntosConNumero.forEach((punto, index) => {
        if (punto.id) {
          idsMap.set(index, punto.id)
        }
      })
      
      // Guardar INMEDIATAMENTE en localStorage ANTES de actualizar el estado
      // Esto evita que el useEffect del contexto sobrescriba con datos vacíos
      try {
        const stored = localStorage.getItem('turutatrack_simulacion')
        const data = stored ? JSON.parse(stored) : {}
        
        const datosParaGuardar = {
          puntosEntrega: puntosConNumero, // Guardar los puntos con IDs
          ruta: data.ruta || [], // Mantener ruta existente si hay
          puntoInicial: puntoIni,
          idsEntrega: Object.fromEntries(idsMap),
          posicionRepartidor: data.posicionRepartidor || null,
          simulando: data.simulando || false
        }
        
          localStorage.setItem('turutatrack_simulacion', JSON.stringify(datosParaGuardar))
          
          // Verificar que se guardó correctamente
          const verificar = localStorage.getItem('turutatrack_simulacion')
          const verificarData = verificar ? JSON.parse(verificar) : null
          
          console.log('✅✅✅ GUARDADO INMEDIATO en localStorage:', {
            puntosCount: datosParaGuardar.puntosEntrega.length,
            idsCount: Object.keys(datosParaGuardar.idsEntrega).length,
            todosLosIds: datosParaGuardar.puntosEntrega.map(p => p.id),
            verificado: verificarData ? {
              puntosCount: verificarData.puntosEntrega?.length || 0,
              idsCount: Object.keys(verificarData.idsEntrega || {}).length
            } : 'NO SE PUDO VERIFICAR'
          })
      } catch (error) {
        console.error('❌ Error guardando inmediatamente:', error)
      }
      
      // Ahora actualizar el estado (esto puede disparar el useEffect pero ya tenemos los datos guardados)
      setPuntosEntrega(puntosConNumero)

      // Calcular ruta optimizada
      const resultadoRuta = await calcularRutaOptimizada(puntos, puntoIni)
      
      setRuta(resultadoRuta.ruta)
      setOrdenOptimizado(resultadoRuta.orden)
      setDistanciaTotal(resultadoRuta.distancia)
      setDuracionTotal(resultadoRuta.duracion)

      // Inicializar posición del repartidor en el punto inicial
      const posicionInicial = { lat: puntoIni.lat, lng: puntoIni.lng }
      setPosicionRepartidor(posicionInicial)
      setIndiceRutaActual(0)
      
      // Guardar todo en localStorage después de calcular la ruta
      setTimeout(() => {
        try {
          // Obtener IDs del contexto actual
          const idsMap = new Map()
          puntosConNumero.forEach((punto, index) => {
            if (punto.id) {
              idsMap.set(index, punto.id)
            }
          })
          
          const datosParaGuardar = {
            puntosEntrega: puntosConNumero,
            ruta: resultadoRuta.ruta || [],
            puntoInicial: puntoIni,
            idsEntrega: Object.fromEntries(idsMap),
            posicionRepartidor: posicionInicial,
            simulando: false
          }
          
          localStorage.setItem('turutatrack_simulacion', JSON.stringify(datosParaGuardar))
          console.log('✅ Datos guardados en localStorage después de calcular ruta:', {
            puntosCount: datosParaGuardar.puntosEntrega.length,
            idsCount: Object.keys(datosParaGuardar.idsEntrega).length,
            primerPuntoId: datosParaGuardar.puntosEntrega[0]?.id,
            todosLosIds: datosParaGuardar.puntosEntrega.map(p => p.id)
          })
        } catch (error) {
          console.error('❌ Error guardando en localStorage:', error)
        }
      }, 500)

      // Notificación de éxito (solo si no está simulando)
      agregarNotificacion('success', 'Ruta calculada exitosamente', false)

    } catch (err) {
      setError(err.message)
      agregarNotificacion('error', `Error: ${err.message}`)
    } finally {
      setCargando(false)
    }
  }

  /**
   * Agrega una notificación al sistema
   * 
   * @param {string} tipo - 'success', 'error', 'info', 'whatsapp', 'inicio-recorrido'
   * @param {string} mensaje - Mensaje de la notificación
   * @param {boolean} mostrarEnSimulacion - Si es false, no se muestra durante la simulación (solo notificaciones importantes)
   */
  const agregarNotificacion = (tipo, mensaje, mostrarEnSimulacion = true) => {
    // Durante la simulación, solo mostrar notificaciones importantes
    if (simulando && !mostrarEnSimulacion) {
      return // No agregar notificaciones no importantes durante la simulación
    }

    const nuevaNotificacion = {
      id: Date.now(),
      tipo, // 'success', 'error', 'info', 'whatsapp', 'inicio-recorrido'
      mensaje,
      timestamp: new Date(),
      mostrarEnSimulacion
    }
    setNotificaciones(prev => [nuevaNotificacion, ...prev].slice(0, 10)) // Máximo 10 notificaciones
  }

  /**
   * Simula el envío de un mensaje de WhatsApp
   */
  const simularWhatsApp = (mensaje) => {
    agregarNotificacion('whatsapp', `📱 WhatsApp: ${mensaje}`)
  }

  /**
   * Calcula la distancia entre dos puntos en metros (fórmula de Haversine)
   */
  const calcularDistancia = (punto1, punto2) => {
    const R = 6371000 // Radio de la Tierra en metros
    const lat1 = punto1.lat * Math.PI / 180
    const lat2 = punto2.lat * Math.PI / 180
    const deltaLat = (punto2.lat - punto1.lat) * Math.PI / 180
    const deltaLng = (punto2.lng - punto1.lng) * Math.PI / 180

    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    return R * c
  }

  /**
   * Encuentra el punto de entrega más cercano a la posición actual
   */
  const encontrarPuntoMasCercano = () => {
    if (!posicionRepartidor || puntosEntrega.length === 0) return -1

    let distanciaMinima = Infinity
    let indiceMasCercano = -1

    puntosEntrega.forEach((punto, index) => {
      const distancia = calcularDistancia(posicionRepartidor, punto)
      if (distancia < distanciaMinima) {
        distanciaMinima = distancia
        indiceMasCercano = index
      }
    })

    return indiceMasCercano
  }

  /**
   * Verifica si el repartidor está cerca de un destino (menos de 100 metros)
   */
  const estaCercaDelDestino = () => {
    if (!posicionRepartidor || puntosEntrega.length === 0) return false

    const puntoMasCercano = encontrarPuntoMasCercano()
    if (puntoMasCercano === -1) return false

    const distancia = calcularDistancia(posicionRepartidor, puntosEntrega[puntoMasCercano])
    return distancia < 100 // 100 metros
  }

  /**
   * Verifica si el repartidor está en camino a algún destino (más de 100m pero menos de 500m)
   */
  const estaEnCamino = () => {
    if (!posicionRepartidor || puntosEntrega.length === 0) return false

    const puntoMasCercano = encontrarPuntoMasCercano()
    if (puntoMasCercano === -1) return false

    const distancia = calcularDistancia(posicionRepartidor, puntosEntrega[puntoMasCercano])
    return distancia >= 100 && distancia < 500 // Entre 100 y 500 metros
  }

  /**
   * Interpola entre dos puntos de la ruta para simular movimiento suave
   */
  const interpolarPunto = (punto1, punto2, factor) => {
    return {
      lat: punto1[0] + (punto2[0] - punto1[0]) * factor,
      lng: punto1[1] + (punto2[1] - punto1[1]) * factor
    }
  }

  /**
   * Calcula el factor de interpolación basado en la velocidad
   * Velocidad más alta = movimiento más rápido
   */
  const calcularFactorVelocidad = () => {
    // Convertir velocidad de km/h a factor de interpolación
    // Velocidad base: 50 km/h = 0.02 factor
    // Fórmula: factor = (velocidad / 50) * 0.02
    return (velocidad / 50) * 0.02
  }

  /**
   * Inicia la simulación automática del movimiento del repartidor
   */
  const iniciarSimulacion = () => {
    if (ruta.length < 2) {
      agregarNotificacion('error', 'Primero debes calcular una ruta')
      return
    }

    setSimulando(true)
    
    // Guardar estado de simulación en localStorage
    setTimeout(() => {
      try {
        const stored = localStorage.getItem('turutatrack_simulacion')
        if (stored) {
          const data = JSON.parse(stored)
          data.simulando = true
          data.posicionRepartidor = posicionRepartidor || data.posicionRepartidor
          localStorage.setItem('turutatrack_simulacion', JSON.stringify(data))
          console.log('✅ Estado de simulación guardado en localStorage')
        }
      } catch (error) {
        console.error('Error guardando estado de simulación:', error)
      }
    }, 100)
    setIndiceRutaActual(0)
    setPosicionRepartidor({ lat: ruta[0][0], lng: ruta[0][1] })

    // Notificación de inicio de recorrido
    agregarNotificacion('inicio-recorrido', '🚚 El repartidor comenzó su recorrido', true)

    if (modoSimulacion === 'automatico') {
      // Simulación automática: se mueve por la ruta automáticamente
      let indice = 0
      let factorInterpolacion = 0

      const mover = () => {
        if (indice >= ruta.length - 1) {
          // Llegamos al final
          detenerSimulacion()
          agregarNotificacion('success', '✅ Ruta completada - Todas las entregas realizadas', true)
          return
        }

        // Interpolar entre el punto actual y el siguiente
        // Usar velocidad dinámica del estado
        factorInterpolacion += calcularFactorVelocidad()
        
        if (factorInterpolacion >= 1) {
          // Pasamos al siguiente punto
          factorInterpolacion = 0
          indice++
          setIndiceRutaActual(indice)
        }

        if (indice < ruta.length - 1) {
          const nuevaPosicion = interpolarPunto(
            ruta[indice],
            ruta[indice + 1],
            factorInterpolacion
          )
          setPosicionRepartidor(nuevaPosicion)

          // Verificar estados y enviar notificaciones usando la nueva posición directamente
          // Esto evita problemas de timing con el estado asíncrono de React
          verificarEstadosYNotificar(nuevaPosicion)
        }

        animacionRef.current = requestAnimationFrame(mover)
      }

      animacionRef.current = requestAnimationFrame(mover)
    } else {
      // Simulación manual: el usuario controla el movimiento
      // No mostrar notificación en modo manual, solo el inicio
    }
  }

  /**
   * Detiene la simulación
   */
  const detenerSimulacion = () => {
    setSimulando(false)
    if (animacionRef.current) {
      cancelAnimationFrame(animacionRef.current)
      animacionRef.current = null
    }
    if (intervaloSimulacionRef.current) {
      clearInterval(intervaloSimulacionRef.current)
      intervaloSimulacionRef.current = null
    }
  }

  /**
   * Verifica los estados del repartidor y envía notificaciones si es necesario
   * Esta función se ejecuta continuamente durante la simulación para detectar
   * cuando el repartidor está cerca de algún destino
   * 
   * @param {Object} posicionActual - Posición actual del repartidor {lat, lng}. 
   *                                   Si no se proporciona, usa el estado posicionRepartidor
   */
  const verificarEstadosYNotificar = (posicionActual = null) => {
    // Usar la posición proporcionada o el estado actual
    const posicion = posicionActual || posicionRepartidor
    
    if (!posicion || puntosEntrega.length === 0) return

    // Verificar cada punto de entrega para ver si está cerca
    puntosEntrega.forEach((punto, index) => {
      const distancia = calcularDistancia(posicion, punto)
      
      // Si está a menos de 100 metros y aún no se ha notificado
      if (distancia < 100 && !destinosNotificadosRef.current.has(index)) {
        // Marcar este destino como notificado
        destinosNotificadosRef.current.add(index)
        
        // Actualizar el punto actual de entrega
        setPuntoActualEntrega(index)
        
        // Enviar notificación de WhatsApp simulada
        const numeroEntrega = punto.numero || index + 1
        simularWhatsApp(`Tu pedido #${numeroEntrega} está llegando, por favor salí a recibirlo`)
      }
      
      // Si está entre 100m y 500m y es el punto más cercano, actualizar estado visual
      if (distancia >= 100 && distancia < 500) {
        // Encontrar el punto más cercano usando la posición actual
        let distanciaMinima = Infinity
        let puntoMasCercano = -1
        
        puntosEntrega.forEach((p, i) => {
          const dist = calcularDistancia(posicion, p)
          if (dist < distanciaMinima) {
            distanciaMinima = dist
            puntoMasCercano = i
          }
        })
        
        if (puntoMasCercano === index && puntoMasCercano !== puntoActualEntrega) {
          setPuntoActualEntrega(index)
        }
      }
    })
  }

  /**
   * Mueve el repartidor manualmente al siguiente punto
   */
  const moverSiguiente = () => {
    if (indiceRutaActual < ruta.length - 1) {
      const nuevoIndice = indiceRutaActual + 1
      const nuevaPosicion = {
        lat: ruta[nuevoIndice][0],
        lng: ruta[nuevoIndice][1]
      }
      setIndiceRutaActual(nuevoIndice)
      setPosicionRepartidor(nuevaPosicion)
      // Verificar estados usando la nueva posición directamente
      verificarEstadosYNotificar(nuevaPosicion)
    }
  }

  /**
   * Mueve el repartidor manualmente al punto anterior
   */
  const moverAnterior = () => {
    if (indiceRutaActual > 0) {
      const nuevoIndice = indiceRutaActual - 1
      const nuevaPosicion = {
        lat: ruta[nuevoIndice][0],
        lng: ruta[nuevoIndice][1]
      }
      setIndiceRutaActual(nuevoIndice)
      setPosicionRepartidor(nuevaPosicion)
      // Verificar estados usando la nueva posición directamente
      verificarEstadosYNotificar(nuevaPosicion)
    }
  }

  /**
   * Callback cuando se arrastra el punto inicial en el mapa
   */
  const handlePuntoInicialCambiado = async (nuevaPosicion) => {
    setPuntoInicial(nuevaPosicion)
    agregarNotificacion('info', 'Punto inicial ajustado manualmente. Recalculando ruta...')
    
    // Recalcular la ruta con el nuevo punto inicial
    if (puntosEntrega.length > 0) {
      try {
        const resultadoRuta = await calcularRutaOptimizada(
          puntosEntrega.map(p => ({ lat: p.lat, lng: p.lng })),
          nuevaPosicion
        )
        setRuta(resultadoRuta.ruta)
        setOrdenOptimizado(resultadoRuta.orden)
        setDistanciaTotal(resultadoRuta.distancia)
        setDuracionTotal(resultadoRuta.duracion)
        agregarNotificacion('success', 'Ruta recalculada con el nuevo punto inicial')
      } catch (error) {
        agregarNotificacion('error', `Error al recalcular ruta: ${error.message}`)
      }
    }
  }

  /**
   * Callback cuando se arrastra un punto de entrega en el mapa
   */
  const handlePuntoEntregaCambiado = async (index, nuevaPosicion) => {
    // Actualizar el punto de entrega
    const nuevosPuntos = [...puntosEntrega]
    nuevosPuntos[index] = {
      ...nuevosPuntos[index],
      lat: nuevaPosicion.lat,
      lng: nuevaPosicion.lng
    }
    setPuntosEntrega(nuevosPuntos)
    agregarNotificacion('info', `Punto de entrega #${nuevosPuntos[index].numero} ajustado. Recalculando ruta...`)
    
    // Recalcular la ruta con el nuevo punto
    if (puntoInicial) {
      try {
        const resultadoRuta = await calcularRutaOptimizada(
          nuevosPuntos.map(p => ({ lat: p.lat, lng: p.lng })),
          puntoInicial
        )
        setRuta(resultadoRuta.ruta)
        setOrdenOptimizado(resultadoRuta.orden)
        setDistanciaTotal(resultadoRuta.distancia)
        setDuracionTotal(resultadoRuta.duracion)
        agregarNotificacion('success', 'Ruta recalculada con el punto ajustado')
      } catch (error) {
        agregarNotificacion('error', `Error al recalcular ruta: ${error.message}`)
      }
    }
  }

  /**
   * Limpia todo y reinicia
   */
  const limpiar = () => {
    detenerSimulacion()
    setDireccionInicial('')
    setDireccionesEntrega([''])
    setPuntoInicial(null)
    setPuntosEntrega([])
    setRuta([])
    setOrdenOptimizado([])
    setDistanciaTotal(0)
    setDuracionTotal(0)
    setPosicionRepartidor(null)
    setIndiceRutaActual(0)
    setPuntoActualEntrega(-1)
    setNotificaciones([])
    setError(null)
    // Limpiar el registro de destinos notificados
    destinosNotificadosRef.current.clear()
  }

  // Limpiar al desmontar el componente
  useEffect(() => {
    return () => {
      detenerSimulacion()
    }
  }, [])

  // Verificación periódica durante la simulación (respaldo)
  // Esto asegura que las notificaciones se envíen incluso si hay algún problema de timing
  useEffect(() => {
    if (!simulando || !posicionRepartidor || puntosEntrega.length === 0) {
      return
    }

    // Verificar cada 300ms durante la simulación como respaldo
    const intervalo = setInterval(() => {
      // Solo verificar si la simulación sigue activa
      if (simulando && posicionRepartidor) {
        verificarEstadosYNotificar()
      }
    }, 300)

    return () => {
      clearInterval(intervalo)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulando])

  return (
    <div className="ruteo-pruebas">
      {/* Header */}
      <header className="header">
        <h1>TuRutaTrack - Pruebas de Ruteo</h1>
        <p>Sistema de optimización de rutas y simulación de reparto</p>
      </header>

      <div className="contenedor-principal">
        {/* Panel lateral izquierdo - Controles */}
        <aside className="panel-controles">
          <div className="seccion">
            <h2>Direcciones</h2>

            {/* Selector de proveedor de geocodificación */}
            <div className="campo">
              <label htmlFor="proveedor-geocodificacion">Proveedor de Geocodificación</label>
              <select
                id="proveedor-geocodificacion"
                value={proveedorGeocodificacion}
                onChange={(e) => setProveedorGeocodificacion(e.target.value)}
                disabled={cargando}
              >
                <option value="auto">Auto (Mejor disponible)</option>
                <option value="google">Google Maps</option>
                <option value="mapbox">Mapbox</option>
                <option value="here">HERE</option>
                <option value="nominatim">Nominatim (OpenStreetMap)</option>
              </select>
              <small style={{ color: 'var(--color-text-light)', fontSize: '0.75rem', display: 'block', marginTop: '0.25rem' }}>
                {proveedorGeocodificacion === 'auto' && 'Usará el mejor proveedor disponible según configuración'}
                {proveedorGeocodificacion === 'google' && (import.meta.env.VITE_GOOGLE_MAPS_API_KEY ? '✓ API key configurada' : '⚠ API key no configurada')}
                {proveedorGeocodificacion === 'mapbox' && (import.meta.env.VITE_MAPBOX_ACCESS_TOKEN ? '✓ Access token configurado' : '⚠ Access token no configurado')}
                {proveedorGeocodificacion === 'here' && (import.meta.env.VITE_HERE_API_KEY ? '✓ API key configurada' : '⚠ API key no configurada')}
                {proveedorGeocodificacion === 'nominatim' && 'Gratis, sin configuración requerida'}
              </small>
            </div>

            {/* Dirección inicial */}
            <div className="campo">
              <label htmlFor="direccion-inicial">Dirección Inicial (Punto de Partida)</label>
              <input
                id="direccion-inicial"
                type="text"
                value={direccionInicial}
                onChange={(e) => setDireccionInicial(e.target.value)}
                placeholder="Ej: San Martin 839, San Miguel de Tucumán, Tucumán, Argentina"
                disabled={cargando}
              />
              {puntoInicial && (
                <small style={{ color: 'var(--color-text-light)', fontSize: '0.8rem', display: 'block', marginTop: '0.25rem' }}>
                  Encontrado: {puntoInicial.direccion}
                </small>
              )}
              <small style={{ color: 'var(--color-text-light)', fontSize: '0.75rem', display: 'block', marginTop: '0.25rem', fontStyle: 'italic' }}>
                Ingresa la dirección completa incluyendo ciudad y provincia para mejor precisión
              </small>
            </div>

            {/* Direcciones de entrega */}
            <div className="campo">
              <label>Direcciones de Entrega</label>
              {direccionesEntrega.map((direccion, index) => (
                <div key={index} className="direccion-item">
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      value={direccion}
                      onChange={(e) => actualizarDireccion(index, e.target.value)}
                      placeholder={`Dirección ${index + 1} (incluir ciudad y provincia)`}
                      disabled={cargando}
                    />
                    {puntosEntrega[index] && (
                      <>
                        <small style={{ color: 'var(--color-text-light)', fontSize: '0.75rem', display: 'block', marginTop: '0.25rem' }}>
                          {puntosEntrega[index].direccion}
                        </small>
                        {puntosEntrega[index].id && (
                          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <input
                              type="text"
                              readOnly
                              value={`${window.location.origin}/seguimiento/${puntosEntrega[index].id}`}
                              style={{ 
                                flex: 1, 
                                padding: '0.25rem 0.5rem', 
                                fontSize: '0.75rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.25rem',
                                backgroundColor: '#f9fafb'
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const url = `${window.location.origin}/seguimiento/${puntosEntrega[index].id}`
                                navigator.clipboard.writeText(url)
                                agregarNotificacion('success', `Enlace copiado al portapapeles`, false)
                              }}
                              className="btn-secundario"
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', minWidth: 'auto' }}
                              title="Copiar enlace de seguimiento"
                            >
                              📋
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {direccionesEntrega.length > 1 && (
                    <button
                      type="button"
                      onClick={() => eliminarDireccion(index)}
                      className="btn-eliminar"
                      disabled={cargando}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={agregarDireccion}
                className="btn-secundario"
                disabled={cargando}
              >
                + Agregar Dirección
              </button>
            </div>

            {/* Botón calcular ruta */}
            <button
              onClick={calcularRuta}
              className="btn-primario"
              disabled={cargando || !direccionInicial.trim()}
            >
              {cargando ? 'Calculando...' : 'Calcular Ruta Optimizada'}
            </button>

            {/* Información de la ruta */}
            {distanciaTotal > 0 && (
              <div className="info-ruta">
                <h3>Información de la Ruta</h3>
                <p><strong>Distancia:</strong> {(distanciaTotal / 1000).toFixed(2)} km</p>
                <p><strong>Duración estimada:</strong> {Math.round(duracionTotal / 60)} minutos</p>
                <p><strong>Puntos de entrega:</strong> {puntosEntrega.length}</p>
              </div>
            )}

            {/* Controles de simulación */}
            {ruta.length > 0 && (
              <div className="seccion">
                <h2>Simulación</h2>
                
                <div className="campo">
                  <label>Modo de Simulación</label>
                  <select
                    value={modoSimulacion}
                    onChange={(e) => {
                      setModoSimulacion(e.target.value)
                      detenerSimulacion()
                    }}
                    disabled={simulando}
                  >
                    <option value="automatico">Automático</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>

                {/* Control de velocidad */}
                <div className="campo">
                  <label htmlFor="velocidad-simulacion">
                    Velocidad: {velocidad} km/h
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      id="velocidad-simulacion"
                      type="range"
                      min="10"
                      max="200"
                      step="5"
                      value={velocidad}
                      onChange={(e) => setVelocidad(parseInt(e.target.value))}
                      disabled={!ruta.length}
                      style={{ flex: 1 }}
                    />
                    {simulando && (
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button
                          type="button"
                          onClick={() => setVelocidad(prev => Math.max(10, prev - 10))}
                          className="btn-secundario"
                          style={{ padding: '0.25rem 0.5rem', minWidth: 'auto' }}
                          title="Disminuir velocidad"
                        >
                          −
                        </button>
                        <button
                          type="button"
                          onClick={() => setVelocidad(prev => Math.min(200, prev + 10))}
                          className="btn-secundario"
                          style={{ padding: '0.25rem 0.5rem', minWidth: 'auto' }}
                          title="Aumentar velocidad"
                        >
                          +
                        </button>
                      </div>
                    )}
                  </div>
                  <small style={{ color: 'var(--color-text-light)', fontSize: '0.75rem', display: 'block', marginTop: '0.25rem' }}>
                    {simulando ? 'Puedes ajustar la velocidad durante la simulación' : 'Ajusta la velocidad antes de iniciar'}
                  </small>
                </div>

                <div className="controles-simulacion">
                  {!simulando ? (
                    <button
                      onClick={iniciarSimulacion}
                      className="btn-primario"
                    >
                      ▶ Iniciar Simulación
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={detenerSimulacion}
                        className="btn-peligro"
                      >
                        ⏸ Detener
                      </button>
                      {modoSimulacion === 'manual' && (
                        <div className="controles-manuales">
                          <button onClick={moverAnterior} className="btn-secundario">
                            ← Anterior
                          </button>
                          <button onClick={moverSiguiente} className="btn-secundario">
                            Siguiente →
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Botón limpiar */}
            <button
              onClick={limpiar}
              className="btn-secundario"
              style={{ marginTop: 'auto' }}
            >
              🗑️ Limpiar Todo
            </button>
          </div>
        </aside>

        {/* Área principal - Mapa */}
        <main className="area-mapa">
          {error && (
            <div className="mensaje-error">
              {error}
            </div>
          )}
          
          {ruta.length === 0 && !cargando && (
            <div className="mensaje-vacio">
              <p>Ingresa las direcciones y calcula la ruta para comenzar</p>
            </div>
          )}

          <div className="mapa-contenedor">
            <MapaRutas
              ruta={ruta}
              puntosEntrega={puntosEntrega}
              puntoInicial={puntoInicial}
              posicionRepartidor={posicionRepartidor}
              puntoActual={puntoActualEntrega}
              onPuntoInicialCambiado={handlePuntoInicialCambiado}
              onPuntoEntregaCambiado={handlePuntoEntregaCambiado}
              seguirRepartidor={simulando}
            />
          </div>
        </main>

        {/* Panel lateral derecho - Notificaciones */}
        <aside className="panel-notificaciones">
          <h2>Notificaciones</h2>
          <div className="notificaciones-lista">
            {notificaciones.length === 0 ? (
              <p className="sin-notificaciones">No hay notificaciones aún</p>
            ) : (
              notificaciones
                .filter(notif => {
                  // Durante la simulación, solo mostrar notificaciones importantes:
                  // - Inicio de recorrido
                  // - Notificaciones de WhatsApp (cuando está cerca)
                  // - Errores
                  // - Ruta completada
                  if (simulando) {
                    return notif.tipo === 'whatsapp' || 
                           notif.tipo === 'inicio-recorrido' || 
                           notif.tipo === 'error' ||
                           (notif.tipo === 'success' && notif.mensaje.includes('completada'))
                  }
                  // Fuera de simulación, mostrar todas
                  return true
                })
                .map(notif => (
                  <div key={notif.id} className={`notificacion notificacion-${notif.tipo}`}>
                    <div className="notificacion-icono">
                      {notif.tipo === 'whatsapp' && '📱'}
                      {notif.tipo === 'inicio-recorrido' && '🚚'}
                      {notif.tipo === 'success' && '✅'}
                      {notif.tipo === 'error' && '❌'}
                      {notif.tipo === 'info' && 'ℹ️'}
                    </div>
                    <div className="notificacion-contenido">
                      <p>{notif.mensaje}</p>
                      <span className="notificacion-tiempo">
                        {notif.timestamp.toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                ))
            )}
          </div>
        </aside>
      </div>
    </div>
  )
}

export default RuteoPruebas

